import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Grid,
  Stack,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllTemplates,
  updateTemplate,
  sendTestEmail,
} from "../../store/modules/emailTemplate/emailTemplateActions";
import { EmailTemplate } from "../../store/repsaApi";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import "./EmailTemplates.css";

const EMAIL_VARIABLES = [
  { label: "Full Name", value: "{{fullName}}" },
  { label: "Email", value: "{{email}}" },
];

const EmailTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const editorRef = useRef<any>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [subject, setSubject] = useState("");
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  // Fetch all templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["emailTemplates"],
    queryFn: getAllTemplates,
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      subject,
      htmlBody,
    }: {
      id: number;
      subject: string;
      htmlBody: string;
    }) => updateTemplate(id, subject, htmlBody),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      setSnackbar({
        open: true,
        message: "Template updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating template: ${error}`,
        severity: "error",
      });
    },
  });

  // Send test email mutation
  const sendTestEmailMutation = useMutation({
    mutationFn: (templateKey: string) => sendTestEmail(templateKey),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: `Test email sent successfully!`,
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error sending test email: ${error}`,
        severity: "error",
      });
    },
  });

  // Initialize GrapesJS editor
  useEffect(() => {
    if (!editorInitialized && selectedTemplate) {
      const editor = grapesjs.init({
        container: "#gjs-editor",
        fromElement: false,
        height: "650px",
        width: "auto",
        storageManager: false,
        plugins: [gjsPresetNewsletter],
        pluginsOpts: {
          "gjs-preset-newsletter": {
            modalTitleImport: "Import template",
          },
        },
        canvas: {
          styles: [],
          scripts: [],
        },
        deviceManager: {
          devices: [
            {
              name: "Desktop",
              width: "",
            },
            {
              name: "Tablet",
              width: "768px",
            },
            {
              name: "Mobile portrait",
              width: "320px",
            },
          ],
        },
        panels: {
          defaults: [
            {
              id: "views",
              buttons: [
                {
                  id: "open-sm",
                  className: "fa fa-paint-brush",
                  command: "open-sm",
                  attributes: { title: "Open Style Manager" },
                  active: true,
                },
                {
                  id: "open-tm",
                  className: "fa fa-cog",
                  command: "open-tm",
                  attributes: { title: "Settings" },
                },
                {
                  id: "open-layers",
                  className: "fa fa-bars",
                  command: "open-layers",
                  attributes: { title: "Open Layer Manager" },
                },
                {
                  id: "open-blocks",
                  className: "fa fa-th-large",
                  command: "open-blocks",
                  attributes: { title: "Open Blocks" },
                },
              ],
            },
          ],
        },
        // Enhanced Style Manager for easier styling
        styleManager: {
          sectors: [
            {
              name: "Typography",
              open: true,
              buildProps: [
                "font-family",
                "font-size",
                "font-weight",
                "letter-spacing",
                "color",
                "line-height",
                "text-align",
                "text-decoration",
              ],
              properties: [
                {
                  name: "Font Family",
                  property: "font-family",
                  type: "select",
                  defaults: "Arial, sans-serif",
                  options: [
                    { id: "arial", value: "Arial, sans-serif", name: "Arial" },
                    {
                      id: "helvetica",
                      value: "Helvetica, sans-serif",
                      name: "Helvetica",
                    },
                    { id: "georgia", value: "Georgia, serif", name: "Georgia" },
                    {
                      id: "times",
                      value: "Times New Roman, serif",
                      name: "Times New Roman",
                    },
                    {
                      id: "courier",
                      value: "Courier New, monospace",
                      name: "Courier New",
                    },
                    {
                      id: "verdana",
                      value: "Verdana, sans-serif",
                      name: "Verdana",
                    },
                    {
                      id: "trebuchet",
                      value: "Trebuchet MS, sans-serif",
                      name: "Trebuchet MS",
                    },
                  ],
                },
                {
                  name: "Font Size",
                  property: "font-size",
                  type: "slider",
                  defaults: "16px",
                  units: ["px", "em", "rem"],
                  min: 8,
                  max: 72,
                },
                {
                  name: "Font Weight",
                  property: "font-weight",
                  type: "select",
                  defaults: "400",
                  options: [
                    { id: "300", value: "300", name: "Light" },
                    { id: "400", value: "400", name: "Normal" },
                    { id: "500", value: "500", name: "Medium" },
                    { id: "600", value: "600", name: "Semi Bold" },
                    { id: "700", value: "700", name: "Bold" },
                    { id: "800", value: "800", name: "Extra Bold" },
                  ],
                },
                {
                  name: "Text Color",
                  property: "color",
                  type: "color",
                  defaults: "#000000",
                },
                {
                  name: "Line Height",
                  property: "line-height",
                  type: "slider",
                  defaults: "1.5",
                  min: 1,
                  max: 3,
                  step: 0.1,
                },
                {
                  name: "Text Align",
                  property: "text-align",
                  type: "radio",
                  defaults: "left",
                  options: [
                    { id: "left", value: "left", name: "Left", title: "Left" },
                    {
                      id: "center",
                      value: "center",
                      name: "Center",
                      title: "Center",
                    },
                    {
                      id: "right",
                      value: "right",
                      name: "Right",
                      title: "Right",
                    },
                    {
                      id: "justify",
                      value: "justify",
                      name: "Justify",
                      title: "Justify",
                    },
                  ],
                },
              ],
            },
            {
              name: "Background & Colors",
              open: true,
              buildProps: ["background-color"],
              properties: [
                {
                  name: "Background Color",
                  property: "background-color",
                  type: "color",
                  defaults: "#ffffff",
                },
              ],
            },
            {
              name: "Spacing",
              open: false,
              buildProps: ["padding", "margin"],
              properties: [
                {
                  name: "Padding",
                  property: "padding",
                  type: "composite",
                  properties: [
                    {
                      name: "Top",
                      property: "padding-top",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Right",
                      property: "padding-right",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Bottom",
                      property: "padding-bottom",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Left",
                      property: "padding-left",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                  ],
                },
                {
                  name: "Margin",
                  property: "margin",
                  type: "composite",
                  properties: [
                    {
                      name: "Top",
                      property: "margin-top",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Right",
                      property: "margin-right",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Bottom",
                      property: "margin-bottom",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                    {
                      name: "Left",
                      property: "margin-left",
                      type: "integer",
                      units: ["px", "%"],
                      min: 0,
                    },
                  ],
                },
              ],
            },
            {
              name: "Border",
              open: false,
              buildProps: ["border-radius", "border"],
              properties: [
                {
                  name: "Border",
                  property: "border",
                  type: "composite",
                  properties: [
                    {
                      name: "Width",
                      property: "border-width",
                      type: "integer",
                      units: ["px"],
                      min: 0,
                      max: 20,
                    },
                    {
                      name: "Style",
                      property: "border-style",
                      type: "select",
                      options: [
                        { id: "none", value: "none", name: "None" },
                        { id: "solid", value: "solid", name: "Solid" },
                        { id: "dotted", value: "dotted", name: "Dotted" },
                        { id: "dashed", value: "dashed", name: "Dashed" },
                      ],
                    },
                    { name: "Color", property: "border-color", type: "color" },
                  ],
                },
                {
                  name: "Border Radius",
                  property: "border-radius",
                  type: "slider",
                  units: ["px", "%"],
                  min: 0,
                  max: 100,
                },
              ],
            },
            {
              name: "Size & Position",
              open: false,
              buildProps: ["width", "height", "max-width"],
              properties: [
                {
                  name: "Width",
                  property: "width",
                  type: "integer",
                  units: ["px", "%"],
                  min: 0,
                },
                {
                  name: "Height",
                  property: "height",
                  type: "integer",
                  units: ["px", "%"],
                  min: 0,
                },
              ],
            },
          ],
        },
      });

      // Add custom variable blocks for each variable
      EMAIL_VARIABLES.forEach((variable) => {
        editor.BlockManager.add(`var-${variable.value}`, {
          label: variable.label,
          category: "Variables",
          content: `<span style="background-color: #ffeb3b; padding: 2px 6px; border-radius: 3px; font-weight: 500; font-family: monospace;">${variable.value}</span>&nbsp;`,
          attributes: {
            title: `Insert ${variable.label}`,
          },
        });
      });

      // Keep only essential RTE actions and remove clutter
      const actionsToKeep = ["bold", "italic", "underline", "link"];
      const allActions = editor.RichTextEditor.getAll();

      allActions.forEach((action: any) => {
        if (!actionsToKeep.includes(action.id)) {
          editor.RichTextEditor.remove(action.id);
        }
      });

      editorRef.current = editor;
      setEditorInitialized(true);

      // Load initial content
      if (selectedTemplate?.htmlBody) {
        editor.setComponents(selectedTemplate.htmlBody);
      }

      // Mobile panel close functionality
      const handleMobilePanelClose = () => {
        if (window.innerWidth <= 768) {
          // Stop all commands to close panels using GrapesJS API
          editor.stopCommand("open-sm");
          editor.stopCommand("open-tm");
          editor.stopCommand("open-layers");
          editor.stopCommand("open-blocks");
        }
      };

      // Add click handler for close button (::before pseudo-element)
      setTimeout(() => {
        const panelContainer = document.querySelector(
          ".gjs-pn-views-container"
        );
        if (panelContainer) {
          panelContainer.addEventListener("click", (e: any) => {
            const rect = panelContainer.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            // Check if click is in the top-right area where close button is
            if (
              clickX > rect.width - 60 &&
              clickY < 60 &&
              window.innerWidth <= 768
            ) {
              handleMobilePanelClose();
            }
          });
        }

        // Close panel when clicking backdrop
        const handleBackdropClick = (e: any) => {
          const panelContainer = document.querySelector(
            ".gjs-pn-views-container"
          );
          const panelButtons = document.querySelector(".gjs-pn-views");

          if (
            panelContainer &&
            panelContainer.classList.contains("gjs-pn-active") &&
            !panelContainer.contains(e.target) &&
            !panelButtons?.contains(e.target) &&
            window.innerWidth <= 768
          ) {
            handleMobilePanelClose();
          }
        };

        document.addEventListener("click", handleBackdropClick);
      }, 500);
    }

    return () => {
      if (editorRef.current && editorInitialized) {
        editorRef.current.destroy();
        setEditorInitialized(false);
      }
    };
  }, [selectedTemplate, editorInitialized]);

  // Handle template selection
  const handleTemplateSelect = (template: EmailTemplate) => {
    // Destroy existing editor if any
    if (editorRef.current) {
      editorRef.current.destroy();
      setEditorInitialized(false);
    }

    setSelectedTemplate(template);
    setSubject(template.subject || "");
  };

  // Handle save
  const handleSave = () => {
    if (selectedTemplate && editorRef.current) {
      const html = editorRef.current.getHtml();
      const css = editorRef.current.getCss();
      const fullHtml = `<style>${css}</style>${html}`;

      updateMutation.mutate({
        id: selectedTemplate.id!,
        subject,
        htmlBody: fullHtml,
      });
    }
  };

  // Handle inserting variable
  const handleInsertVariable = (variable: string) => {
    if (editorRef.current) {
      const selected = editorRef.current.getSelected();

      // Create a styled variable span
      const variableHtml = `<span style="background-color: #ffeb3b; padding: 2px 6px; border-radius: 3px; font-weight: 500; font-family: monospace;">${variable}</span>&nbsp;`;

      if (selected) {
        const componentType = selected.get("type");

        // If text component is selected, safely append to existing content
        if (componentType === "text") {
          try {
            // Use components() to safely append without replacing
            const currentHtml = selected.toHTML();
            selected.components(currentHtml + variableHtml);
          } catch (e) {
            // Fallback: append as new component after the selected one
            selected
              .parent()
              ?.append(variableHtml, { at: selected.index() + 1 });
          }
        } else if (componentType === "textnode") {
          // If it's a text node, append to its parent
          const parent = selected.parent();
          if (parent) {
            parent.append(variableHtml);
          }
        } else {
          // For other components (div, table cell, etc.), append as child
          try {
            selected.append(variableHtml);
          } catch (e) {
            // If append fails, try adding after the selected component
            selected
              .parent()
              ?.append(variableHtml, { at: selected.index() + 1 });
          }
        }
      } else {
        // No selection - add to the canvas
        const wrapper = editorRef.current.getWrapper();
        if (wrapper) {
          wrapper.append(variableHtml);
        }
      }

      setSnackbar({
        open: true,
        message: `Variable ${variable} inserted!`,
        severity: "success",
      });
    }
  };

  // Handle test email
  const handleSendTestEmail = () => {
    if (selectedTemplate?.templateKey) {
      sendTestEmailMutation.mutate(selectedTemplate.templateKey);
    }
  };

  return (
    <>
      <Box sx={{ mb: 6, ml: 1 }}>
        <Typography
          color="primary"
          variant="h4"
          component="h1"
          fontWeight="bold"
          gutterBottom
        >
          Email Templates
        </Typography>

        <Grid container spacing={3}>
          {/* Template List */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ p: 2, height: "calc(100vh - 250px)", overflow: "auto" }}
            >
              <Typography variant="h6" gutterBottom>
                Templates
              </Typography>
              {templatesLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {templates.map((template) => (
                    <ListItem key={template.id} disablePadding>
                      <ListItemButton
                        selected={selectedTemplate?.id === template.id}
                        onClick={() => handleTemplateSelect(template)}
                      >
                        <ListItemText
                          primary={template.templateKey}
                          secondary={template.description}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Editor */}
          <Grid item xs={12} md={9}>
            {selectedTemplate ? (
              <Paper sx={{ p: 3 }}>
                <Stack spacing={2}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">
                      Edit Template: {selectedTemplate.templateKey}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={handleSendTestEmail}
                        disabled={
                          !selectedTemplate || sendTestEmailMutation.isPending
                        }
                      >
                        {sendTestEmailMutation.isPending
                          ? "Sending..."
                          : "Test Email"}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={updateMutation.isPending}
                      >
                        {updateMutation.isPending ? "Saving..." : "Save"}
                      </Button>
                    </Stack>
                  </Box>

                  {selectedTemplate.description && (
                    <Alert severity="info">
                      {selectedTemplate.description}
                    </Alert>
                  )}

                  {/* Subject */}
                  <TextField
                    fullWidth
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    variant="outlined"
                  />

                  {/* <Alert severity="success" sx={{ bgcolor: "#f1f8e9" }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      📌 Quick Insert Variables (Click any chip below):
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      {EMAIL_VARIABLES.map((variable) => (
                        <Chip
                          key={variable.value}
                          label={`${variable.label}`}
                          size="small"
                          onClick={() => handleInsertVariable(variable.value)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: "#ffeb3b",
                            fontWeight: 500,
                            fontFamily: "monospace",
                            "&:hover": {
                              bgcolor: "#fdd835",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        />
                      ))}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      💡 You can also drag variables from the "Variables"
                      category in the blocks panel on the left
                    </Typography>
                  </Alert> */}

                  <Box
                    id="gjs-editor"
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  />

                  {/* <Alert severity="info">
                    <Typography variant="caption" component="div">
                      💡 <strong>Tips:</strong>
                      <ul
                        style={{
                          marginTop: 4,
                          marginBottom: 0,
                          paddingLeft: 20,
                        }}
                      >
                        <li>
                          Drag blocks from the left panel to build your email
                        </li>
                        <li>
                          Click any element to edit its properties in the right
                          panel
                        </li>
                        <li>
                          Use the <strong>"Variables"</strong> category (left
                          panel) to drag and drop variables
                        </li>
                        <li>
                          Variables like{" "}
                          <code
                            style={{
                              backgroundColor: "#ffeb3b",
                              padding: "0 4px",
                            }}
                          >
                            {"{{firstName}}"}
                          </code>{" "}
                          will be replaced with actual data when emails are sent
                        </li>
                      </ul>
                    </Typography>
                  </Alert> */}
                </Stack>
              </Paper>
            ) : (
              <Paper sx={{ p: 3, textAlign: "center", minHeight: 400 }}>
                <Typography color="text.secondary">
                  Select a template from the list to edit
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default EmailTemplates;
